import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, Plus, Pencil, Trash2 } from 'lucide-react';
import { Heading } from '../layout';
import { SettingsSplit, TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { ColorPaletteField } from '../forms/ColorPaletteField';
import { useUIColors } from '../../hooks/useTheme';
import procurementCatalogsAPI from '../../services/procurementCatalogsAPI';
import {
  CATALOG_DEFINITIONS,
  CATALOG_GROUPS,
  codeFromLabel,
  toUiCatalogRow,
} from './procurementCatalogDefaults';

const GROUP_ICONS = {
  master: Layers,
};

const EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  sort_order: 10,
  color_hex: '#2563eb',
  is_active: 'Sí',
};

const emptyGrouped = () =>
  CATALOG_DEFINITIONS.reduce((acc, def) => {
    acc[def.key] = [];
    return acc;
  }, {});

const MinimalCatalogTable = ({ rows = [], readOnly = false, onAdd, onEdit, onDelete }) => {
  const ui = useUIColors();
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
          String(a.label).localeCompare(String(b.label), 'es')
      ),
    [rows]
  );

  const allSelected = sortedRows.length > 0 && selectedIds.size === sortedRows.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(sortedRows.map((row) => row.id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    const selected = sortedRows.filter((row) => selectedIds.has(row.id));
    onDelete?.(selected);
  };

  const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '9px 10px',
    fontSize: '13px',
    color: ui.text,
    borderBottom: `1px solid color-mix(in srgb, ${ui.border} 70%, transparent)`,
    verticalAlign: 'middle',
  };

  const iconBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: ui.muted,
    cursor: 'pointer',
  };

  return (
    <div>
      {readOnly ? null : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            marginBottom: '8px',
          }}
        >
          {selectedIds.size > 0 ? (
            <button
              type="button"
              onClick={handleBulkDelete}
              style={{
                ...iconBtn,
                width: 'auto',
                padding: '0 10px',
                gap: 6,
                color: '#b91c1c',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Trash2 size={14} />
              Eliminar ({selectedIds.size})
            </button>
          ) : null}
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 30,
              padding: '0 10px',
              borderRadius: 6,
              border: `1px solid ${ui.border}`,
              background: ui.panel || 'transparent',
              color: ui.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Plus size={14} />
            Agregar
          </button>
        </div>
      )}

      <div style={{ overflowX: 'auto', border: `1px solid ${ui.border}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {readOnly ? null : (
                <th style={{ ...thStyle, width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
              )}
              <th style={thStyle}>Código</th>
              <th style={thStyle}>Etiqueta</th>
              <th style={thStyle}>Descripción</th>
              <th style={thStyle}>Orden</th>
              <th style={thStyle}>Activo</th>
              {readOnly ? null : <th style={{ ...thStyle, width: 80 }} />}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={readOnly ? 5 : 7}
                  style={{ ...tdStyle, color: ui.muted, textAlign: 'center' }}
                >
                  {readOnly ? 'Sin estados definidos.' : 'Sin parámetros. Agrega el primero.'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.id}>
                  {readOnly ? null : (
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleOne(row.id)}
                      />
                    </td>
                  )}
                  <td style={tdStyle}>
                    <code style={{ fontSize: 12 }}>{row.code}</code>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: row.color_hex || '#2563eb',
                          flexShrink: 0,
                        }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: ui.muted }}>{row.description || '—'}</td>
                  <td style={tdStyle}>{row.sort_order}</td>
                  <td style={tdStyle}>{row.is_active}</td>
                  {readOnly ? null : (
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button type="button" style={iconBtn} onClick={() => onEdit?.(row)} aria-label="Editar">
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          style={{ ...iconBtn, color: '#b91c1c' }}
                          onClick={() => onDelete?.([row])}
                          aria-label="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GroupCatalogPanel = ({
  groupKey,
  selectedCatalogByGroup,
  setSelectedCatalogByGroup,
  catalogItemsByKey,
  openAdd,
  openEdit,
  handleDelete,
}) => {
  const ui = useUIColors();
  const definitions = CATALOG_DEFINITIONS.filter((item) => item.group === groupKey);
  const selectedCatalogKey = selectedCatalogByGroup[groupKey] || definitions[0]?.key;
  const selectedDefinition = definitions.find((item) => item.key === selectedCatalogKey) || definitions[0];

  return (
    <SettingsSplit
      ariaLabel="Catálogos"
      items={definitions.map((definition) => ({ key: definition.key, label: definition.label }))}
      activeKey={selectedCatalogKey}
      onChange={(key) =>
        setSelectedCatalogByGroup((prev) => ({
          ...prev,
          [groupKey]: key,
        }))
      }
    >
      {selectedDefinition ? (
        <>
          <div style={{ marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>
              {selectedDefinition.label}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted, lineHeight: 1.4 }}>
              {selectedDefinition.description}
            </p>
          </div>
          <MinimalCatalogTable
            rows={catalogItemsByKey[selectedDefinition.key] || []}
            readOnly={Boolean(selectedDefinition.readOnly)}
            onAdd={() => openAdd(selectedDefinition.key)}
            onEdit={(row) => openEdit(selectedDefinition.key, row)}
            onDelete={(rows) => handleDelete(selectedDefinition.key, rows)}
          />
        </>
      ) : null}
    </SettingsSplit>
  );
};

const PaintProcurementConfigWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  void isDarkMode;

  const [catalogItemsByKey, setCatalogItemsByKey] = useState(emptyGrouped);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedCatalogByGroup, setSelectedCatalogByGroup] = useState({
    master: 'payment_conditions',
    orders: 'document_types',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingCatalogKey, setEditingCatalogKey] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const loadCatalogs = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const grouped = await procurementCatalogsAPI.getGrouped();
      const next = emptyGrouped();
      Object.keys(next).forEach((key) => {
        next[key] = (grouped?.[key] || []).map(toUiCatalogRow);
      });
      setCatalogItemsByKey(next);
    } catch (error) {
      console.error('Error loading procurement catalogs:', error);
      setLoadError(error?.message || 'No se pudieron cargar los catálogos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  const editingDefinition = CATALOG_DEFINITIONS.find((item) => item.key === editingCatalogKey);

  const openAdd = (catalogKey) => {
    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);
    if (definition?.readOnly) return;
    const current = catalogItemsByKey[catalogKey] || [];
    const nextOrder =
      current.length > 0 ? Math.max(...current.map((row) => Number(row.sort_order || 0))) + 10 : 10;
    setEditingCatalogKey(catalogKey);
    setEditingItemId(null);
    setFormError('');
    setFormData({
      ...EMPTY_FORM,
      sort_order: nextOrder,
    });
    setShowForm(true);
  };

  const openEdit = (catalogKey, rowToEdit) => {
    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);
    if (definition?.readOnly || !rowToEdit) return;
    setEditingCatalogKey(catalogKey);
    setEditingItemId(rowToEdit.id);
    setFormError('');
    setFormData({
      code: rowToEdit.code || '',
      label: rowToEdit.label || '',
      description: rowToEdit.description || '',
      sort_order: Number(rowToEdit.sort_order || 10),
      color_hex: rowToEdit.color_hex || '#2563eb',
      is_active: rowToEdit.is_active || 'Sí',
    });
    setShowForm(true);
  };

  const handleDelete = async (catalogKey, payload) => {
    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);
    if (definition?.readOnly) return;
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    const ids = rowsToDelete.map((row) => row?.id).filter(Boolean);
    if (!ids.length) return;
    const ok = window.confirm(
      ids.length === 1
        ? '¿Desactivar este parámetro del catálogo?'
        : `¿Desactivar ${ids.length} parámetros del catálogo?`
    );
    if (!ok) return;
    try {
      if (ids.length === 1) {
        await procurementCatalogsAPI.delete(ids[0]);
      } else {
        await procurementCatalogsAPI.bulkDelete(ids);
      }
      await loadCatalogs();
    } catch (error) {
      window.alert(error?.message || 'No se pudo eliminar el parámetro.');
    }
  };

  const handleSave = async () => {
    const label = String(formData.label || '').trim();
    let code = String(formData.code || '').trim();
    if (!label) {
      setFormError('La etiqueta es obligatoria.');
      return;
    }
    if (!code) code = codeFromLabel(label);
    if (!code) {
      setFormError('Código y etiqueta son obligatorios.');
      return;
    }
    const catalogKey = editingCatalogKey;
    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);
    if (!catalogKey || definition?.readOnly) return;

    const body = {
      catalog_key: catalogKey,
      code,
      label,
      description: String(formData.description || '').trim(),
      sort_order: Number(formData.sort_order || 0),
      color_hex: formData.color_hex || '#2563eb',
      is_active_label: formData.is_active || 'Sí',
    };

    setIsSaving(true);
    setFormError('');
    try {
      if (editingItemId) {
        await procurementCatalogsAPI.update(editingItemId, body);
      } else {
        await procurementCatalogsAPI.create(body);
      }
      setShowForm(false);
      setEditingItemId(null);
      setEditingCatalogKey('');
      setFormData({ ...EMPTY_FORM });
      await loadCatalogs();
    } catch (error) {
      setFormError(error?.message || 'No se pudo guardar el parámetro.');
    } finally {
      setIsSaving(false);
    }
  };

  const formSections = [
    {
      key: 'basic',
      title: 'Parámetro',
      fields: [
        { key: 'code', label: 'Código' },
        { key: 'label', label: 'Etiqueta' },
        { key: 'description', label: 'Descripción', fullWidth: true },
        { key: 'sort_order', label: 'Orden', type: 'number' },
        {
          key: 'color_hex',
          label: 'Color',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange, colors }) => (
            <ColorPaletteField
              value={value}
              onChange={onChange}
              label="Color"
              muted={colors.muted}
              text={colors.text}
              border={colors.inputBorder || colors.sectionBorder}
            />
          ),
        },
        {
          key: 'is_active',
          label: 'Activo',
          type: 'select',
          options: [
            { value: 'Sí', label: 'Sí' },
            { value: 'No', label: 'No' },
          ],
        },
      ],
    },
  ];

  const tabs = CATALOG_GROUPS.map((group) => {
    const Icon = GROUP_ICONS[group.key] || Layers;
    return {
      key: group.key,
      label: group.label,
      icon: <Icon size={16} />,
      content: (
        <GroupCatalogPanel
          groupKey={group.key}
          selectedCatalogByGroup={selectedCatalogByGroup}
          setSelectedCatalogByGroup={setSelectedCatalogByGroup}
          catalogItemsByKey={catalogItemsByKey}
          openAdd={openAdd}
          openEdit={openEdit}
          handleDelete={handleDelete}
        />
      ),
    };
  });

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: '6px' }}>
          Ajustes Adquisiciones
        </Heading>
        <p style={{ margin: 0, maxWidth: 640, fontSize: '13px', color: ui.muted, lineHeight: 1.45 }}>
          Define los valores de las listas usadas en proveedores. Los cambios se guardan en la base de
          datos.
        </p>
      </div>

      {loading ? (
        <p style={{ color: ui.muted, fontSize: 13 }}>Cargando catálogos…</p>
      ) : loadError ? (
        <p style={{ color: '#b91c1c', fontSize: 13 }}>{loadError}</p>
      ) : (
        <TabNavigation tabs={tabs} defaultTab="master" isDarkMode={isDarkMode} />
      )}

      <FormDrawer
        isOpen={showForm}
        onClose={() => !isSaving && setShowForm(false)}
        onSave={handleSave}
        isDarkMode={isDarkMode}
        title={editingItemId ? 'Editar parámetro' : 'Nuevo parámetro'}
        subtitle={editingDefinition?.label || 'Catálogo'}
        sections={formSections}
        formData={formData}
        onChange={(key, value) => {
          setFormData((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'label' && !editingItemId && !String(prev.code || '').trim()) {
              next.code = codeFromLabel(value);
            }
            return next;
          });
        }}
        formError={formError}
        isSaving={isSaving}
        width={540}
      />
    </div>
  );
};

export default PaintProcurementConfigWorkspace;
